import React from 'react';
import PageWrapper from '../../components/PageWrapper';
import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Typography, useTheme, Grid2, Chip } from '@mui/material';
import { useSalesOrderGetInfoQuery, useSalesOrderSetStatusShippedMutation, useSalesOrderSetStatusNotShippedMutation, useSalesOrderResendOrderConfirmationEmailMutation } from '../../services/salesOrders';
import { useAuthEffects } from '../../components/Auth';
import useAuth from '../../components/Auth';
import { QuickOrderTotals } from '../orders/components/OrderPaymentTotals';
import { translateSalesOrderStatus, translateUOM } from '../../i18n/utils';
import { useTranslation } from 'react-i18next';
import { OrderStatus, OrderProduct, CURRENCY_SYMBOLS, Currencies } from '../../types';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { PlanListItemElV2 } from '../../components/PlanList';
import { SimpleList } from '../../components/SimpleList';
import EmailIcon from '@mui/icons-material/Email';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '../../constants';

const SalesOrderDetailView = () => {
	useAuthEffects();
	const navigate = useNavigate();
	const {id} = useParams();
	const {userProfile} = useAuth();
	const {t} = useTranslation();
	const [salesOrderSetStatusShippedMutation] = useSalesOrderSetStatusShippedMutation();
	const [salesOrderSetStatusNotShippedMutation] = useSalesOrderSetStatusNotShippedMutation();
	const [salesOrderResendOrderConfirmationEmailMutation, {isLoading: isResendingConfirmation, isSuccess: isConfirmationResent}] = useSalesOrderResendOrderConfirmationEmailMutation();
	const {data: order, isLoading, refetch} = useSalesOrderGetInfoQuery(id as string, {skip: !id || !userProfile});
	const theme = useTheme();

	const redirectToHome = () => {
		navigate('/');
	};

	useEffect(() => {
		if (userProfile && !isLoading && !order) {
			redirectToHome();
		}
	}, [isLoading, userProfile, order]);

	const handleSetStatusShippedClick = () => {
		if (id) {
			salesOrderSetStatusShippedMutation({ orderId: id }).unwrap().then(() => {
				refetch();
			});
		}
	};

	const handleSetStatusNotShippedClick = () => {
		if (id) {
			salesOrderSetStatusNotShippedMutation({ orderId: id }).unwrap().then(() => {
				refetch();
			});
		}
	};

	const handleResendOrderConfirmationEmailClick = () => {
		if (id) {
			salesOrderResendOrderConfirmationEmailMutation({ orderId: id });
		}
	};

	return <PageWrapper isLoading={!order} loadingMessage="Loading order..." maxWidth='md'>
		<Box display='flex' flexDirection='column' gap={2} mt={2}>
			<div>
				<Button startIcon={<ChevronLeftIcon />} component={Link} to='/sales'>{t('salesOrderDetailView.backToSales')}</Button>
			</div>
		</Box>

		{order && <div>
			<Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" gap={2} p={4}>
				<Typography variant="h4">{t('salesOrderDetailView.orderId', {orderId: order?.id})}</Typography>
				<Typography variant="h6"><Chip label={translateSalesOrderStatus(order?.status)} sx={{p: 1, borderRadius: 0}} color={order?.status === OrderStatus.Shipped ? 'success' : 'warning'} /></Typography>
			</Box>

			<Grid2 container spacing={2} mb={4}>
				<Grid2 size={{xs: 12, md: 6}}>
					<Box display='flex' flexDirection='column' gap={2}>
						<Box>
							<Typography variant='body2' py={1}>
								{t('salesOrderDetailView.orderDate')}
							</Typography>
							<Typography>
								{dayjs(order.createdAt).format(DATE_FORMAT)}
							</Typography>
						</Box>

						<Box>
							<Typography variant='body2' py={1}>
								{t('salesOrderDetailView.customer')}
							</Typography>
							<Typography>
								{order.customerFirstName} {order.customerLastName}
							</Typography>
							<Typography>
								{order.phone}
							</Typography>
							<Typography>
								{order.customerEmail}
							</Typography>

							{Boolean(order && !order.isOrderReceiptSent) && <Button 
								disabled={isResendingConfirmation || isConfirmationResent} size='small' startIcon={<EmailIcon />} onClick={handleResendOrderConfirmationEmailClick} variant='text'>
								{isResendingConfirmation && 'Resending...'}
								{isConfirmationResent && 'Confirmation resent'}
								{!isResendingConfirmation && !isConfirmationResent && 'Resend Order Confirmation'}
							</Button>}
						</Box>
						<Box>
							<Typography variant='body2' py={1}>
								{t('salesOrderDetailView.shippingAddress')}
							</Typography>
							<Typography>
								{order.customerAddress1}
							</Typography>
							<Typography>
								{order.customerAddress2}
							</Typography>
							<Typography>
								<Typography>
									{order.customerCity}
								</Typography>
								<Typography>
									{order.customerState}
								</Typography>
								{order.customerPostalCode}
							</Typography>
						</Box>
						<Box>
							<Typography variant='body2' py={1}>
								{t('salesOrderDetailView.seller')}
							</Typography>
							<Typography>
								{order.vendorName}
							</Typography>
							<Typography>
								{order.vendorAddress}
							</Typography>
							{order.vendorTaxId && <Typography>
								{order.vendorTaxId}
							</Typography>}
						</Box>
					</Box>
				</Grid2>
				<Grid2 size={{xs: 8, md: 6}} offset={{xs: 0, md: 0}}>
					<Box>
						<Typography variant='body2'>
							{t('salesOrderDetailView.plan')}
						</Typography>
						{order.plan && <Box mt={1}>
							<PlanListItemElV2 item={order.plan} />
						</Box>}
					</Box>
				</Grid2>
			</Grid2>

			<Typography variant='body2'>
				{t('salesOrderDetailView.products')}
			</Typography>
			<SimpleList 
				items={(order.products || []).map((product: OrderProduct) => ({
					id: product.productId,
					imgUrl: product.imgUrl,
					label: product.name,
					href: `/products/${product.productId}`,
					secondary: `${CURRENCY_SYMBOLS[order.currency as Currencies]}${product.pricePerUnit}${product.bundleProductId ? ' - included in bundle' : ''}`,
					properties: [
						...(product.isBundle ? [{
							label: 'Bundle',
						}] : []), {
							label: product.quantity,
							secondary: translateUOM(product.uom),
							sx: {
								minWidth: theme.spacing(5),
								textAlign: 'right',
							}
						}],
					groupId: product.bundleProductId ? product.bundleProductId : undefined,
				}))}
			/>
			<QuickOrderTotals 
				products={order.products.filter(product => !product.bundleProductId)}
				additionalFees={order.additionalFees || []}
				currency={order.currency}
			/>
			{order.status === OrderStatus.Paid && <Box py={2} display='flex' flexDirection='column' gap={1}>
				<Typography my={1}>
					{t('salesOrderDetailView.orderAwaitsShipping')}
				</Typography>
				<div>
					<Button size='large' startIcon={<AssignmentTurnedInIcon />} onClick={handleSetStatusShippedClick} variant='contained' color='success'>{t('salesOrderDetailView.markAsShipped')}</Button>
				</div>
			</Box>}
			{order.status === OrderStatus.Shipped && <Box py={2} display='flex' flexDirection='column' gap={1}>
				<Typography my={1}>
					{t('salesOrderDetailView.orderShipped')}
				</Typography>
				<div>
					<Button size='large' startIcon={<AssignmentTurnedInIcon />} onClick={handleSetStatusNotShippedClick} variant='contained' color='error'>{t('salesOrderDetailView.markAsNotShipped')}</Button>
				</div>
			</Box>}
		</div>}
	</PageWrapper>;
};

export default SalesOrderDetailView;